import { cloneValue, getAtPath, mergeValues, setAtPath } from './configPaths'
import { applyPreset, collectionById, formatConfiguration } from './schemaV2'
import { isSliderV3, sliderElementLimitReached } from './sliderValidation'

const IMAGE_POSITIONS = {
  'vertical-left': 'left',
  'vertical-right': 'right',
  'horizontal-top': 'top',
  'horizontal-bottom': 'bottom',
  'full-image': 'full',
  imageless: 'none',
  canvas: 'canvas',
}

const POSITION_LAYOUTS = {
  left: 'vertical-left',
  right: 'vertical-right',
  top: 'horizontal-top',
  bottom: 'horizontal-bottom',
}

const LEGACY_PRESET_PATHS = [
  'format.id',
  'design.stylePackId',
  'design.layout',
  'design.imagePosition',
  'design.readabilityOverlay',
  'design.edgeStyle',
  'design.pattern',
  'design.patternColor',
  'design.buttonStyle',
  'design.glow',
  'brand.alignment',
  'brand.size',
]

// An option can switch on what belongs to it (Split Slider's Event Ad turns on a countdown). A `*` in the target
// means any collection item: nothing changes when one already has it, otherwise the first item gets it.
// Leaving the option switches those targets off again on every item.
function applyOptionSets(next, path, value, template, previous) {
  const field = (template?.schema?.sections || []).flatMap((section) => section.fields || []).find((entry) => entry.path === path)
  const left = previous !== value ? (field?.options || []).find((option) => option.value === previous)?.sets : null
  Object.entries(left || {}).forEach(([target, setValue]) => {
    if (setValue !== true) return
    const [head, tail] = target.split('.*.')
    const targets = tail ? (getAtPath(next, head) || []).map((_, index) => `${head}.${index}.${tail}`) : [target]
    targets.forEach((resolved) => { if (getAtPath(next, resolved)) next = setAtPath(next, resolved, false) })
  })
  const sets = (field?.options || []).find((option) => option.value === value)?.sets
  Object.entries(sets || {}).forEach(([target, setValue]) => {
    const [head, tail] = target.split('.*.')
    const items = tail ? getAtPath(next, head) : null
    if (tail && (!Array.isArray(items) || !items.length || items.some((item) => getAtPath(item, tail)))) return
    const resolved = tail ? `${head}.0.${tail}` : target
    if (getAtPath(next, resolved)) return
    if (setValue === true && sliderElementLimitReached(next, resolved)) {
      const slide = resolved.replace(/\.[^.]+\.enabled$/, '')
      const drop = ['info', 'bullets', 'kicker', 'subline'].find((key) => getAtPath(next, `${slide}.${key}.enabled`))
      if (drop) next = setAtPath(next, `${slide}.${drop}.enabled`, false)
    }
    next = setAtPath(next, resolved, setValue)
  })
  return next
}

export function applyEditorFieldValue(current, path, value, template) {
  if (!current) return current
  if (value === true && sliderElementLimitReached(current, path)) return current
  let next = applyOptionSets(setAtPath(current, path, value), path, value, template, getAtPath(current, path))

  if (current.schemaVersion === 'studio-editor-v2/ad-v4') {
    if (path === 'format.id') {
      const format = template?.manifest?.formats?.find((entry) => entry.id === value)
      if (format) {
        next = setAtPath(next, 'format', formatConfiguration(format))
        if (isSliderV3(current)) {
          // Format changes preserve the style layer, including manual axis edits.
          const suffix = String(current.presetId || '').replace(`${current.format.id}-`, '')
          const matching = `${value}-${suffix}`
          next = setAtPath(next, 'presetId', current.presetId && template.presets?.[matching] ? matching : null)
          return next
        }
        const currentPreset = template?.presets?.[getAtPath(next, 'presetId')]
        if (currentPreset?.formatId !== value) {
          const reference = (template?.manifest?.presets || []).find(
            (entry) => template?.presets?.[entry.id]?.formatId === value,
          )
          next = reference
            ? applyPreset(next, template.presets[reference.id])
            : setAtPath(next, 'presetId', null)
        }
      }
    } else if (path === 'presetId' && template?.presets?.[value]) {
      next = applyPreset(next, template.presets[value])
    } else if (path !== 'presetId') {
      const activePreset = template?.presets?.[getAtPath(next, 'presetId')]
      if ((activePreset?.controlledPaths || []).includes(path)) {
        next = setAtPath(next, 'presetId', null)
      }
    }
    return next
  }

  if (path === 'format.id') {
    const format = template?.manifest?.formats?.find((entry) => entry.id === value)
    if (format) {
      next = setAtPath(next, 'format', {
        id: format.id,
        width: format.width,
        height: format.height,
      })
      if (!format.layouts.includes(getAtPath(next, 'design.layout'))) {
        next = setAtPath(next, 'design.layout', format.layouts[0])
        next = setAtPath(next, 'design.imagePosition', IMAGE_POSITIONS[format.layouts[0]] || 'left')
      }
    }
  }

  if (path === 'design.presetId' && template?.presets?.[value]) {
    next = mergeValues(next, template.presets[value])
    next = setAtPath(next, 'design.presetId', value)
    const format = template?.manifest?.formats?.find(
      (entry) => entry.id === getAtPath(next, 'format.id'),
    )
    if (format && !format.layouts.includes(getAtPath(next, 'design.layout'))) {
      next = setAtPath(next, 'design.layout', format.layouts[0])
    }
    next = setAtPath(
      next,
      'design.imagePosition',
      IMAGE_POSITIONS[getAtPath(next, 'design.layout')] || 'left',
    )
  }

  if (path === 'design.compositionPresetId' && template?.presets?.[value]) {
    next = mergeValues(next, template.presets[value])
    next = setAtPath(next, 'design.compositionPresetId', value)
  }

  if (path === 'design.layout') {
    next = setAtPath(next, 'design.imagePosition', IMAGE_POSITIONS[value] || 'left')
  }

  if (path === 'design.imagePosition' && POSITION_LAYOUTS[value]) {
    next = setAtPath(next, 'design.layout', POSITION_LAYOUTS[value])
  }

  const presetControlled = LEGACY_PRESET_PATHS.includes(path)
    || path.startsWith('design.colors.')
    || path.startsWith('animation.')
  if (
    path !== 'design.compositionPresetId'
    && getAtPath(next, 'design.compositionPresetId', null) != null
    && presetControlled
  ) {
    next = setAtPath(next, 'design.compositionPresetId', 'custom')
  }

  return next
}

export function editorCollectionDescriptor(template, collectionId) {
  const generic = collectionById(template?.schema, collectionId)
  if (generic) return generic
  if (collectionId !== 'slides') return null
  const collection = (template?.schema?.collections || []).find((entry) => entry.path === 'slides')
  if (!collection) return null
  return {
    ...collection,
    id: 'slides',
    identityPath: collection.identityPath || 'id',
    itemDefaults: cloneValue(template?.defaults?.slides?.[0] || {}),
    addable: true,
    removable: true,
    reorderable: true,
    duplicable: true,
  }
}

export function assistantImageField(schema, absolutePath) {
  for (const section of schema?.sections || []) {
    for (const field of section.fields || []) {
      if (field.path === absolutePath && field.type === 'image-editor') return field
    }
  }
  for (const collection of schema?.collections || []) {
    const prefix = `${collection.path}.`
    if (!absolutePath.startsWith(prefix)) continue
    const remainder = absolutePath.slice(prefix.length)
    const separator = remainder.indexOf('.')
    if (separator < 1) continue
    const relativePath = remainder.slice(separator + 1)
    for (const section of collection.itemSections || []) {
      const field = (section.fields || []).find(
        (entry) => entry.path === relativePath && entry.type === 'image-editor',
      )
      if (field) return field
    }
  }
  return null
}

export function itemIndexById(items, identityPath, itemId) {
  return (items || []).findIndex(
    (item) => String(getAtPath(item, identityPath, '')) === String(itemId),
  )
}
